import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { Product } from '../types'

interface UseProductsOptions {
  page?: number
  limit?: number
  search?: string
  brand?: string
  nutriscoreGrade?: string
}

export function useProducts(client: SupabaseClient | null, options: UseProductsOptions = {}) {
  const { page = 1, limit = 20, search = '', brand, nutriscoreGrade } = options

  const [products, setProducts] = useState<Product[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProducts = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('products')
        .select('*', { count: 'exact' })

      // Apply search filter
      if (search) {
        query = query.or(`product_name.ilike.%${search}%,brands.ilike.%${search}%,code.ilike.%${search}%`)
      }

      // Apply brand filter
      if (brand) {
        query = query.ilike('brands', `%${brand}%`)
      }

      // Apply nutriscore filter
      if (nutriscoreGrade) {
        query = query.eq('nutriscore_grade', nutriscoreGrade)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to).order('product_name', { ascending: true })

      const { data, error: queryError, count } = await query

      if (queryError) throw queryError

      setProducts(data || [])
      setTotal(count || 0)
    } catch (err: any) {
      console.error('Error fetching products:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, page, limit, search, brand, nutriscoreGrade])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  return { products, total, isLoading, error, refetch: fetchProducts }
}

export function useProductDetail(client: SupabaseClient | null, productCode: string | null) {
  const [product, setProduct] = useState<Product | null>(null)
  const [reviews, setReviews] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!client || !productCode) {
      setIsLoading(false)
      return
    }

    const supabaseClient = client

    async function fetchProductDetail() {
      setIsLoading(true)
      setError(null)

      try {
        // Fetch product
        const { data: productData, error: productError } = await supabaseClient
          .from('products')
          .select('*')
          .eq('code', productCode)
          .single()

        if (productError) throw productError
        setProduct(productData)

        // Fetch reviews
        const { data: reviewsData } = await supabaseClient
          .from('product_reviews')
          .select('*')
          .eq('product_code', productCode)
          .order('created_at', { ascending: false })
          .limit(10)

        setReviews(reviewsData || [])
      } catch (err: any) {
        console.error('Error fetching product detail:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchProductDetail()
  }, [client, productCode])

  return { product, reviews, isLoading, error }
}
